import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import Countdown from "@/components/Countdown";
import ProblemSelector from "@/components/ProblemSelector";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock3, MapPin, Users, Sparkles, ShieldCheck, ChevronDown, Languages } from "lucide-react";
import useSeo from "@/hooks/useSeo";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const HOST_FALLBACK = "https://images.unsplash.com/photo-1758336011136-343678e4fc05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB0ZWFjaGVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0NTQ5MjE0fDA&ixlib=rb-4.1.0&q=85";

export default function PublicWebinar() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [customBrand, setCustomBrand] = useState(null); // { name, brand_logo, host_bio, host } — set when on a verified custom domain
  const [notFoundOnDomain, setNotFoundOnDomain] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [regDone, setRegDone] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [lang, setLang] = useState("en");

  // SEO — dynamic per webinar (title + description of the workshop)
  useSeo({
    title: w ? `${w.title} — Register on Videoepreneur` : undefined,
    description: w ? (w.description?.slice(0, 155) || `Register for ${w.title} — a live webinar on Videoepreneur.`) : undefined,
    image: w?.cover_image,
    type: "article",
  });

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        // Detect if we're on a custom domain (i.e. host doesn't match the platform host baked into REACT_APP_BACKEND_URL)
        let platformHost = "";
        try { platformHost = new URL(process.env.REACT_APP_BACKEND_URL).host; } catch { /* noop */ }
        const currentHost = window.location.host;
        const isCustomDomain = platformHost && currentHost && currentHost !== platformHost;

        if (isCustomDomain) {
          try {
            const { data: brand } = await axios.get(`${API_BASE}/public/domain-lookup`, { params: { host: currentHost } });
            setCustomBrand(brand);
          } catch {
            // Domain not configured → still render, but without brand override
          }
        }

        const { data } = await axios.get(`${API_BASE}/public/webinars/${slug}`, {
          params: isCustomDomain ? { host: currentHost } : {},
        });
        setW(data);
        // capture UTM to state via sessionStorage
        ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
          const v = params.get(k);
          if (v) sessionStorage.setItem(k, v);
        });
      } catch (e) {
        if (e.response?.status === 404) setNotFoundOnDomain(true);
        toast.error(e.response?.data?.detail || "Webinar not found");
      }
    })();
  }, [slug]);

  if (notFoundOnDomain) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="vp-label mb-3">404</div>
        <h1 className="font-display text-3xl font-bold">This webinar isn't available here.</h1>
        <p className="text-[#4A4D57] mt-3 max-w-md">This host may have moved or unpublished the session. Try opening the link you were sent again.</p>
      </div>
    );
  }
  if (!w) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  const price = w.price || 0;
  const isPaid = price > 0;
  // Pick localized copy if available, else fall back to English
  const i18n = w.landing_copy_i18n || {};
  const lp = (lang !== "en" && i18n[lang]) ? i18n[lang] : (w.landing_copy || {});
  const hasHi = !!(i18n.hi && Object.keys(i18n.hi).length > 0);
  // On custom domain, prefer the custom brand for the top-nav name/logo (still keep w.host inside the "your host" card so attendees know who's teaching)
  const navName = customBrand?.brand?.name || w.host?.name || "Host";
  const navLogo = customBrand?.brand?.brand_logo || w.host?.brand_logo;
  const isCustomDomain = !!customBrand;

  return (
    <div className="min-h-screen vp-hero-bg relative">
      {/* Nav */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {navLogo ? <img src={navLogo} alt="brand" className="w-9 h-9 rounded-xl object-cover" /> :
            <div className="w-9 h-9 rounded-xl bg-[#FFFFFF] border border-[#0B5CFF]/40 flex items-center justify-center" />}
          <span className="font-display font-semibold">{navName}</span>
        </div>
        <button data-testid={TIDS.publicRegisterCta} onClick={() => setModalOpen(true)} className="vp-btn-primary text-sm">
          Register {isPaid ? `· ${fmtINR(price)}` : "Free"}
        </button>
      </div>

      {/* Language toggle — only visible if a Hindi translation exists */}
      {hasHi && (
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex justify-end">
          <div className="inline-flex bg-white border border-[#E4E6EC] rounded-full p-1 gap-1 text-xs" role="tablist">
            {["en", "hi"].map((k) => (
              <button
                key={k}
                onClick={() => setLang(k)}
                data-testid={TIDS.langToggle + "-" + k}
                className={"px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors " + (lang === k ? "bg-[#0B5CFF] text-white" : "text-[#4A4D57] hover:text-[#0B0D17]")}
              >
                <Languages className="w-3.5 h-3.5" strokeWidth={1.75} />
                {k === "en" ? "English" : "हिन्दी"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-6 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 vp-fade-up">
          <div className="vp-chip mb-5"><span className="w-2 h-2 rounded-full bg-[#0B5CFF]" /> Live webinar · {isPaid ? fmtINR(price) : "Free"}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            {lp.headline || w.title}
          </h1>
          <p className="mt-5 text-[#4A4D57] text-lg leading-relaxed max-w-xl">{lp.subheadline || w.description}</p>

          <div className="mt-8">
            <div className="vp-label mb-3">Starts in</div>
            <Countdown target={w.scheduled_at} />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid={TIDS.publicRegisterCta + "-hero"} onClick={() => setModalOpen(true)} className="vp-btn-primary inline-flex items-center gap-2">
              Register now
            </button>
            <div className="flex items-center gap-2 text-sm text-[#747680]">
              <Users className="w-4 h-4" strokeWidth={1.5} /> {w.registrations_count} already registered
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4 text-sm text-[#4A4D57]">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" strokeWidth={1.5} /> {new Date(w.scheduled_at).toLocaleString("en-IN")}</span>
            <span className="inline-flex items-center gap-1.5"><Clock3 className="w-4 h-4" strokeWidth={1.5} /> {w.duration_min} min</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={1.5} /> Live on Zoom</span>
          </div>
        </div>

        <div className="lg:col-span-5 vp-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="vp-glass p-6 relative">
            <img src={w.cover_image || HOST_FALLBACK} alt="host" className="w-full aspect-[4/3] rounded-2xl object-cover" />
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="vp-label">Your host</div>
                <div className="font-display font-semibold text-lg">{w.host?.name}</div>
                <div className="text-sm text-[#4A4D57] mt-1">{w.host?.host_bio || "Sharing what actually works."}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Selector */}
      {(w.problem_tags?.length > 0 || true) && (
        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-16">
          <ProblemSelector onChange={setSelectedProblem} />
        </section>
      )}

      {/* What you'll learn */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-8">
        <div>
          <div className="vp-label mb-3">What you'll learn</div>
          <ul className="space-y-3">
            {(lp.promise || []).map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-[#0B0D17]">
                <Sparkles className="w-4 h-4 mt-1 text-[#0B5CFF]" strokeWidth={1.5} /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="vp-label mb-3">Who this is for</div>
          <ul className="space-y-3">
            {(lp.who_is_this_for || []).map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-[#0B0D17]">
                <ShieldCheck className="w-4 h-4 mt-1 text-[#0B5CFF]" strokeWidth={1.5} /> {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Agenda */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-8">
        <div className="vp-label mb-4">Agenda</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(lp.agenda || []).map((a, i) => (
            <div key={i} className="vp-glass p-5">
              <div className="font-mono text-[#0B5CFF] text-sm">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-display font-semibold">{a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Urgency */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12">
        <div className="vp-glass p-8 border-[#0B5CFF]/40">
          <div className="vp-label" style={{ color: "#0B5CFF" }}>Urgency</div>
          <div className="font-display text-2xl font-bold mt-2">{lp.urgency_block || "Limited seats. Doors close 10 minutes before the session begins."}</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-5 sm:px-8 py-12">
        <div className="vp-label mb-4 text-center">Questions</div>
        <div className="space-y-3">
          {(lp.faq || []).map((f, i) => (
            <details key={i} className="vp-glass p-5 group">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-display font-semibold">{f.q}</span>
                <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="text-[#4A4D57] mt-3 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 vp-glass-strong p-3 z-40 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-display font-semibold">{isPaid ? fmtINR(price) : "Free"}</div>
          <div className="text-xs text-[#747680]">{new Date(w.scheduled_at).toLocaleDateString("en-IN")}</div>
        </div>
        <button onClick={() => setModalOpen(true)} className="vp-btn-primary flex-1 text-center" data-testid={TIDS.publicRegisterCta + "-mobile"}>
          Register now
        </button>
      </div>

      {/* Modal */}
      <RegisterDialog w={w} open={modalOpen} onOpenChange={setModalOpen} selectedProblem={selectedProblem}
        regDone={regDone} setRegDone={setRegDone} payLoading={payLoading} setPayLoading={setPayLoading} />

      {/* Footer branding — hidden on white-label custom domains */}
      {!isCustomDomain && (
        <footer className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10 border-t border-[#E4E6EC] mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#747680]">
            <div>© {new Date().getFullYear()} {w.host?.name || "Host"}</div>
            <a href="https://videoepreneur.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#0B0D17] transition-colors" data-testid="powered-by-vp">
              <span>Powered by</span>
              <span className="font-display font-semibold text-[#0B0D17]">Videoepreneur</span>
            </a>
          </div>
        </footer>
      )}

      <div className="h-24 lg:h-8" />
    </div>
  );
}

function RegisterDialog({ w, open, onOpenChange, selectedProblem, regDone, setRegDone, payLoading, setPayLoading }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [country, setCountry] = useState("+91");
  const [city, setCity] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true); setCouponError(""); setCouponInfo(null);
    try {
      const { data } = await axios.post(`${API_BASE}/public/validate-coupon`, {
        webinar_id: w.id, code: couponCode.trim().toUpperCase(),
      });
      setCouponInfo(data);
      toast.success(`Applied — save ${fmtINR(data.discount)}`);
    } catch (e) {
      setCouponError(e.response?.data?.detail || "Invalid code");
    } finally { setValidatingCoupon(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/register`, {
        webinar_id: w.id,
        name, email, whatsapp, country_code: country, city,
        problem_tags: selectedProblem ? [selectedProblem] : [],
        coupon_code: couponInfo?.code || null,
        utm_source: sessionStorage.getItem("utm_source"),
        utm_medium: sessionStorage.getItem("utm_medium"),
        utm_campaign: sessionStorage.getItem("utm_campaign"),
      });
      setRegDone(data);
      if (!data.paid_required) toast.success("You're registered! Check your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSubmitting(false); }
  };

  const payNow = async () => {
    setPayLoading(true);
    try {
      // Simulate razorpay flow success (mock)
      await new Promise((r) => setTimeout(r, 900));
      await axios.post(`${API_BASE}/public/payment/mock`, { registration_id: regDone.registration.id, success: true });
      toast.success("Payment successful. Confirmation sent.");
      setRegDone({ ...regDone, paid_required: false, paid: true });
    } catch (e) { toast.error("Payment failed. Try again."); }
    finally { setPayLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E6EC] text-[#0B0D17] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{regDone ? "You're in." : "Reserve your seat"}</DialogTitle>
          <DialogDescription className="text-[#4A4D57]">
            {regDone ? "Check your email + WhatsApp for the join link." : "Free workshop preview · limited seats"}
          </DialogDescription>
        </DialogHeader>

        {!regDone ? (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <div className="vp-label mb-1.5">Your name</div>
              <input data-testid={TIDS.publicRegisterName} required className="vp-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <div className="vp-label mb-1.5">Email</div>
              <input data-testid={TIDS.publicRegisterEmail} required type="email" className="vp-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <div className="vp-label mb-1.5">WhatsApp</div>
              <div className="flex gap-2">
                <input className="vp-input w-24" value={country} onChange={(e) => setCountry(e.target.value)} />
                <input data-testid={TIDS.publicRegisterWhatsapp} required className="vp-input flex-1" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="10-digit number" />
              </div>
            </div>
            <div>
              <div className="vp-label mb-1.5">City (optional)</div>
              <input data-testid={TIDS.publicRegisterCity} className="vp-input" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            {w.price > 0 && (
              <div>
                <div className="vp-label mb-1.5">Coupon code (optional)</div>
                <div className="flex gap-2">
                  <input className="vp-input flex-1 uppercase" placeholder="e.g. SAVE20" value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value); setCouponInfo(null); setCouponError(""); }}
                    data-testid="public-coupon-input" />
                  <button type="button" onClick={applyCoupon} disabled={validatingCoupon || !couponCode.trim()}
                    className="vp-btn-ghost text-sm" data-testid="public-coupon-apply-btn">
                    {validatingCoupon ? "…" : "Apply"}
                  </button>
                </div>
                {couponInfo && (
                  <div className="text-xs text-[#1E8E3E] mt-1.5">
                    ✓ {couponInfo.code} applied · save {fmtINR(couponInfo.discount)} · pay {fmtINR(couponInfo.final_price)}
                  </div>
                )}
                {couponError && <div className="text-xs text-[#E02D3C] mt-1.5">{couponError}</div>}
              </div>
            )}
            <button data-testid={TIDS.publicRegisterSubmit} type="submit" disabled={submitting} className="vp-btn-primary w-full">
              {submitting ? "Reserving…" : (w.price > 0 ? `Continue · ${fmtINR(couponInfo?.final_price ?? w.price)}` : "Reserve my seat")}
            </button>
          </form>
        ) : regDone.paid_required ? (
          <div className="space-y-4 mt-2">
            <div className="vp-glass p-4">
              <div className="vp-label mb-1">Amount</div>
              <div className="font-display text-3xl font-bold">{fmtINR(w.price)}</div>
              <div className="text-xs text-[#747680] mt-1">Razorpay · UPI / cards / netbanking (mock)</div>
            </div>
            <button data-testid={TIDS.publicPayNow} onClick={payNow} disabled={payLoading} className="vp-btn-gold w-full">
              {payLoading ? "Processing…" : "Pay now"}
            </button>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="text-[#0B0D17]">Your unique join link is on the way via email + WhatsApp.</div>
            <div className="vp-card p-3 text-xs break-all">{regDone.registration?.join_url}</div>
            <a
              href={`/w/${w.slug}/live?r=${encodeURIComponent(regDone.registration?.id || "")}&e=${encodeURIComponent(email)}`}
              data-testid="public-join-live-link"
              className="vp-btn-primary text-sm w-full text-center inline-block"
            >
              🎥 Join live session
            </a>
            {regDone.portal_token && (
              <a
                href={`/portal/${regDone.portal_token}`}
                data-testid="public-portal-link"
                className="vp-btn-ghost text-sm w-full text-center inline-block"
              >
                Open my attendee portal
              </a>
            )}
            <button onClick={() => onOpenChange(false)} className="vp-btn-primary w-full">Done</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
